import React, { useEffect, useState, useRef } from "react";
import ApiService from "../../../service/ApiService";
import { apiPaths } from "../../../service/apiPaths";
import {
  formatDateToISODateString,
  limit,
  updateComponent,
  ViewType,
} from "../../../util/constant";
import toast from "react-hot-toast";
import { SubmitHandler } from "react-hook-form";
import { IAddHiring } from "./Hiring.props";
import { useNavigate, useLocation } from "react-router-dom";

export const noticePeriodOptions = [
  { id: "", name: "All" },
  { id: "Immediate", name: "Immediate" },
  { id: "10-15 days", name: "10-15 days" },
  { id: "30 days", name: "30 days" },
  { id: "60 days", name: "60 days" },
  { id: "90 days", name: "90 days" },
];

export const statusOptions = [
  { id: "", name: "All" },
  { id: "Follow-Up", name: "Follow-Up" },
  { id: "Hold", name: "Hold" },
  { id: "Schedule", name: "Schedule" },
  { id: "Rejection", name: "Rejection" },
  { id: "High Salary", name: "High Salary" },
  { id: "HR Rejection", name: "HR Rejection" },
  { id: "HR Action", name: "HR Action" },
  { id: "TL Action", name: "TL Action" },
  { id: "Call NA", name: "Call NA" },
  { id: "Practical Needed", name: "Practical Needed" },
];

export const technologyOptions = [
  { id: "", name: "All" },
  { id: "Node JS", name: "Node JS" },
  { id: "React native", name: "React native" },
  { id: "HR Executive", name: "HR Executive" },
  { id: "React JS", name: "React JS" },
  { id: "BDE", name: "BDE" },
  { id: "BDM", name: "BDM" },
  { id: "QA", name: "QA" },
];

const useHiring = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize all filters from URL on mount
  const getInitialFiltersFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      page: Number(params.get("page")) || 1,
      noticePeriod: params.get("noticePeriod") || "",
      status: params.get("status") || "",
      technology: params.get("technology") || "",
      search: params.get("search") || "",
    };
  };

  const initialFilters = getInitialFiltersFromURL();

  const [allhired, setAllHired] = useState<any[]>([]);
  const [search, setSearch] = useState<string>(initialFilters.search);
  const [page, setPage] = useState<number>(initialFilters.page);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [sortValue, setSortValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const [hiringId, setHiringId] = useState<string | null>(null);
  const [noticePeriodFilter, setNoticePeriodFilter] = useState<string>(
    initialFilters.noticePeriod
  );
  const [statusFilter, setStatusFilter] = useState<string>(
    initialFilters.status
  );
  const [technologyFilter, setTechnologyFilter] = useState<string>(
    initialFilters.technology
  );
  const isSearchTriggeredRef = useRef<boolean>(false);
  const isUrlSyncRef = useRef<boolean>(false);
  const hasInitialLoadRef = useRef<boolean>(false);
  const isFilterChangeRef = useRef<boolean>(false);
  const updatingHiringIdsRef = useRef<Set<string>>(new Set());
  const [addData, setAddData] = useState<IAddHiring[]>([]);

  // Helper function to update URL with current filters
  const updateURL = (updates: {
    page?: number;
    noticePeriod?: string;
    status?: string;
    technology?: string;
    search?: string;
  }) => {
    const params = new URLSearchParams();

    // Get current values or use updates
    const currentPage = updates.page !== undefined ? updates.page : page;
    const currentNoticePeriod =
      updates.noticePeriod !== undefined
        ? updates.noticePeriod
        : noticePeriodFilter;
    const currentStatus =
      updates.status !== undefined ? updates.status : statusFilter;
    const currentTechnology =
      updates.technology !== undefined ? updates.technology : technologyFilter;
    const currentSearch =
      updates.search !== undefined ? updates.search : search;

    // Always set page
    params.set("page", currentPage.toString());

    // Only add filter params if they have values
    if (currentNoticePeriod) params.set("noticePeriod", currentNoticePeriod);
    if (currentStatus) params.set("status", currentStatus);
    if (currentTechnology) params.set("technology", currentTechnology);
    if (currentSearch) params.set("search", currentSearch);

    const newURL = `?${params.toString()}`;
    console.log("Updating URL to:", newURL);
    navigate(newURL, { replace: true });
  };

  const uploadResume = async (file: File) => {
    const formData = new FormData();
    formData.append("resume", file);

    try {
      const result = await ApiService({
        method: "POST",
        endpoint: `${apiPaths.uploadResume}`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = result?.resumeUrl || result?.fileUrl || result?.url;
      if (url) {
        setResumeUrl(url);
        return url;
      }
      return null;
    } catch (error) {
      toast.error("Resume upload failed");
      return null;
    }
  };

  const getAllHired = async (
    fromDate: Date | null,
    toDate: Date | null,
    pageOverride?: number
  ) => {
    try {
      const params = new URLSearchParams();

      if (fromDate && toDate) {
        const formattedFromDate = formatDateToISODateString(fromDate);
        const formattedToDate = formatDateToISODateString(toDate);
        params.append("fromDate", formattedFromDate);
        params.append("toDate", formattedToDate);
      }

      const pageToUse = pageOverride !== undefined ? pageOverride : page;
      params.append("page", pageToUse.toString());
      params.append("limit", limit.toString());

      // Only add filters if they have values (not empty strings)
      if (search && search.trim()) {
        params.append("search", search.trim());
      }

      if (noticePeriodFilter && noticePeriodFilter !== "") {
        params.append("noticePeriod", noticePeriodFilter);
      }

      if (statusFilter && statusFilter !== "") {
        params.append("status", statusFilter);
      }

      if (technologyFilter && technologyFilter !== "") {
        params.append("technology", technologyFilter);
      }

      const endpoint = `${apiPaths.getAllHired}?${params.toString()}`;
      console.log("Fetching with endpoint:", endpoint);

      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });

      setAllHired(result.hiring || []);
      setTotalPage(result.totalPages || 1);
      setResumeUrl(result.fileUrl);
    } catch (error) {
      console.error("API error:", error);
      toast.error("Failed to get data");
    }
  };

  const handleConfirmLeadStatusUp = async (
    id: any,
    status: any,
    reminderDate?: Date,
    rejectionNote?: string
  ) => {
    const hiringId = id._id;
    if (updatingHiringIdsRef.current.has(hiringId)) {
      console.log(
        "Update already in progress for this hiring record, skipping duplicate call"
      );
      return;
    }

    updatingHiringIdsRef.current.add(hiringId);
    setLoading(true);

    try {
      const currentRecord = await ApiService({
        method: "GET",
        endpoint: apiPaths.getHiring + id._id,
      });
      const currentHiring = currentRecord.hiring || {};

      const data: any = { status };
      const oldStatus = currentHiring.status || id.status || "";
      const newStatus = status || "";
      if (newStatus && oldStatus !== newStatus) {
        const statusChangeNote = oldStatus
          ? `${oldStatus} -> ${newStatus}`
          : `Follow-Up -> ${newStatus}`;
        const existingHrNote = currentHiring.hrNote || id.hrNote || "";
        const lines = existingHrNote
          .split("\n")
          .filter((line: string) => line.trim() !== "");
        const lastLine = lines.length > 0 ? lines[lines.length - 1] : "";
        const alreadyHasStatusChange = lastLine.includes(statusChangeNote);

        if (!alreadyHasStatusChange) {
          const timestamp = formatTimestamp();
          const noteWithTimestamp = `[${timestamp}] ${statusChangeNote}`;
          const updatedHrNote = existingHrNote
            ? `${existingHrNote}\n${noteWithTimestamp}`
            : noteWithTimestamp;

          data.hrNote = updatedHrNote;
        }
      }

      if (rejectionNote !== undefined && rejectionNote.trim()) {
        const timestamp = formatTimestamp();
        const noteWithTimestamp = `[${timestamp}] ${rejectionNote.trim()}`;
        const existingRejectionNote =
          currentHiring.rejectionNote || id.rejectionNote || "";
        const updatedRejectionNote = existingRejectionNote
          ? `${existingRejectionNote}\n${noteWithTimestamp}`
          : noteWithTimestamp;

        data.rejectionNote = updatedRejectionNote;
      }
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.editHiring}${id._id}`,
        data: data,
      });
      await getAllHired(null, null);
      const updated = await ApiService({
        method: "GET",
        endpoint: apiPaths.getHiring + id._id,
      });
      Object.assign(id, updated.hiring);
      toast.success("Status updated successfully");
    } catch (error: any) {
      console.error("Error updating status:", error);
      toast.error(error?.response?.data?.message || "Error updating status");
    } finally {
      setLoading(false);
      updatingHiringIdsRef.current.delete(hiringId);
    }
  };

  const handleSelectStatus = (e: any, id: any, rejectionNote?: string) => {
    const status = e.target.value;
    const reminderDate = id.reminderDate;
    handleConfirmLeadStatusUp(id, status, reminderDate, rejectionNote);
  };

  const handleStatusChange = (e: any, params: any) => {
    handleSelectStatus(e, params.row);
  };

  // When filters change, reset to page 1 and update URL
  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      return;
    }

    console.log(
      "Filters changed - notice:",
      noticePeriodFilter,
      "status:",
      statusFilter,
      "tech:",
      technologyFilter
    );
    isFilterChangeRef.current = true;
    setPage(1);
    updateURL({
      page: 1,
      noticePeriod: noticePeriodFilter,
      status: statusFilter,
      technology: technologyFilter,
      search: search,
    });
    getAllHired(null, null, 1);

    setTimeout(() => {
      isFilterChangeRef.current = false;
    }, 100);
  }, [noticePeriodFilter, statusFilter, technologyFilter]);

  const detectNoticePeriodFromSearch = (searchTerm: string): string => {
    if (!searchTerm || !searchTerm.trim()) {
      return "";
    }

    const trimmedSearch = searchTerm.trim().toLowerCase();

    for (const option of noticePeriodOptions) {
      if (option.id === "") continue;

      const optionNameLower = option.name.toLowerCase();
      const optionIdLower = option.id.toLowerCase();

      if (
        trimmedSearch === optionNameLower ||
        trimmedSearch === optionIdLower
      ) {
        return option.id;
      }

      const daysMatch = optionIdLower.match(/(\d+)\s*days?/);
      if (daysMatch) {
        const days = daysMatch[1];
        if (
          trimmedSearch === days ||
          trimmedSearch === `${days} days` ||
          trimmedSearch === `${days} day`
        ) {
          return option.id;
        }
      }

      if (
        optionIdLower === "immediate" &&
        (trimmedSearch === "immediate" || trimmedSearch === "0")
      ) {
        return option.id;
      }
    }

    return "";
  };

  // When search changes, reset to page 1 and update URL
  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      return;
    }

    console.log("Search changed:", search);
    isSearchTriggeredRef.current = true;
    setPage(1);
    updateURL({
      page: 1,
      search: search,
      noticePeriod: noticePeriodFilter,
      status: statusFilter,
      technology: technologyFilter,
    });

    const detectedNoticePeriod = detectNoticePeriodFromSearch(search);
    if (detectedNoticePeriod && noticePeriodFilter !== detectedNoticePeriod) {
      setNoticePeriodFilter(detectedNoticePeriod);
    }

    getAllHired(null, null, 1);
    setTimeout(() => {
      isSearchTriggeredRef.current = false;
    }, 100);
  }, [search]);

  // Initial load: Read all filters from URL
  useEffect(() => {
    const currentSearch = window.location.search;
    const params = new URLSearchParams(currentSearch);
    const urlPage = Number(params.get("page")) || 1;
    const urlNoticePeriod = params.get("noticePeriod") || "";
    const urlStatus = params.get("status") || "";
    const urlTechnology = params.get("technology") || "";
    const urlSearchParam = params.get("search") || "";

    console.log("=== INITIAL LOAD ===");
    console.log("URL:", window.location.href);
    console.log("Filters from URL:", {
      page: urlPage,
      noticePeriod: urlNoticePeriod,
      status: urlStatus,
      technology: urlTechnology,
      search: urlSearchParam,
    });

    // Set all states to match URL
    setPage(urlPage);
    setNoticePeriodFilter(urlNoticePeriod);
    setStatusFilter(urlStatus);
    setTechnologyFilter(urlTechnology);
    setSearch(urlSearchParam);

    // Load data with URL filters
    getAllHired(null, null, urlPage);
    hasInitialLoadRef.current = true;
  }, []);

  const previousLocationRef = useRef<string>(
    location.pathname + location.search
  );

  // Sync with URL when location changes (e.g., returning from edit page)
  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      previousLocationRef.current = location.pathname + location.search;
      return;
    }

    const currentSearch = window.location.search;
    const currentPathname = window.location.pathname;
    const currentLocation = currentPathname + currentSearch;
    const previousLocation = previousLocationRef.current;
    const locationChanged = previousLocation !== currentLocation;

    console.log("=== LOCATION CHANGE DETECTED ===");
    console.log("Previous:", previousLocation);
    console.log("Current:", currentLocation);
    console.log("Changed:", locationChanged);

    if (currentPathname === "/hiring" && locationChanged) {
      const params = new URLSearchParams(currentSearch);
      const urlPage = Number(params.get("page")) || 1;
      const urlNoticePeriod = params.get("noticePeriod") || "";
      const urlStatus = params.get("status") || "";
      const urlTechnology = params.get("technology") || "";
      const urlSearchParam = params.get("search") || "";

      console.log("Restoring filters from URL:", {
        page: urlPage,
        noticePeriod: urlNoticePeriod,
        status: urlStatus,
        technology: urlTechnology,
        search: urlSearchParam,
      });

      const previousPathname = previousLocation.split("?")[0];
      const isReturningFromEdit = previousPathname.includes("/edit-hiring");

      console.log("Is returning from edit?", isReturningFromEdit);

      // Always restore filters when location changes and we're on /hiring
      isUrlSyncRef.current = true;
      setPage(urlPage);
      setNoticePeriodFilter(urlNoticePeriod);
      setStatusFilter(urlStatus);
      setTechnologyFilter(urlTechnology);
      setSearch(urlSearchParam);

      console.log("Loading data with restored filters...");
      getAllHired(null, null, urlPage);

      setTimeout(() => {
        isUrlSyncRef.current = false;
      }, 100);
    }

    previousLocationRef.current = currentLocation;
  }, [location.pathname, location.search]);

  // When page changes (pagination), update URL and reload
  useEffect(() => {
    if (
      isSearchTriggeredRef.current ||
      isUrlSyncRef.current ||
      isFilterChangeRef.current ||
      !hasInitialLoadRef.current
    ) {
      console.log("Skipping page effect - triggered by:", {
        search: isSearchTriggeredRef.current,
        urlSync: isUrlSyncRef.current,
        filterChange: isFilterChangeRef.current,
        notInitialized: !hasInitialLoadRef.current,
      });
      return;
    }

    console.log("Page changed to:", page);
    updateURL({ page: page });
    getAllHired(null, null);
  }, [page]);

  const handleSubmit: SubmitHandler<IAddHiring> = async () => {
    const result = await ApiService({
      method: "POST",
      endpoint: apiPaths.createHiring,
      data: addData,
    });
    setAddData(result);
  };

  const handleEdit = (id: string) => {
    // Build query string with all current filters
    const params = new URLSearchParams();
    params.set("page", page.toString());
    if (noticePeriodFilter) params.set("noticePeriod", noticePeriodFilter);
    if (statusFilter) params.set("status", statusFilter);
    if (technologyFilter) params.set("technology", technologyFilter);
    if (search) params.set("search", search);

    console.log("Navigating to edit with filters:", params.toString());
    navigate(`/edit-hiring/${id}?${params.toString()}`);
  };

  const onSubmit = async (id: string, data: any) => {
    setLoading(true);
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.editHiring}${id}`,
        data: data,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (): string => {
    const now = new Date();
    const date = now.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    const time = now.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${date} ${time}`;
  };

  const handleAddHrNote = async (
    id: string,
    hrNote: string,
    existingHrNote?: string
  ) => {
    if (!hrNote || !hrNote.trim()) {
      toast.error("Please enter a note");
      return;
    }

    setLoading(true);
    try {
      const timestamp = formatTimestamp();
      const noteWithTimestamp = `[${timestamp}] ${hrNote.trim()}`;

      const updatedNote = existingHrNote
        ? `${existingHrNote}\n${noteWithTimestamp}`
        : noteWithTimestamp;

      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.editHiring}${id}`,
        data: { hrNote: updatedNote },
      });
      toast.success("HR Note added successfully");
      await getAllHired(null, null);
    } catch (error: any) {
      console.error("Error adding HR note:", error);
      toast.error(error?.response?.data?.message || "Error adding HR note");
    } finally {
      setLoading(false);
    }
  };

  const handleAddRaviNote = async (
    id: string,
    raviNote: string,
    existingRaviNote?: string
  ) => {
    if (!raviNote || !raviNote.trim()) {
      toast.error("Please enter a note");
      return;
    }

    setLoading(true);
    try {
      const timestamp = formatTimestamp();
      const noteWithTimestamp = `[${timestamp}] ${raviNote.trim()}`;

      const updatedNote = existingRaviNote
        ? `${existingRaviNote}\n${noteWithTimestamp}`
        : noteWithTimestamp;

      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.editHiring}${id}`,
        data: { raviNote: updatedNote },
      });
      toast.success("Ravi Note added successfully");
      await getAllHired(null, null);
    } catch (error: any) {
      console.error("Error adding Ravi note:", error);
      toast.error(error?.response?.data?.message || "Error adding Ravi note");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await ApiService({
        method: "DELETE",
        endpoint: `${apiPaths.deleteHiring}${id}`,
      });
      toast.success("Employee deleted successfully");
      await getAllHired(null, null)
    } catch (error) {
      toast.error("Something went wrong")
    }
  };

  return {
    allhired,
    page,
    resumeUrl,
    totalPage,
    loading,
    handleDelete,
    onSubmit,
    handleSubmit,
    getAllHired,
    handleEdit,
    setSearch,
    setPage,
    handleStatusChange,
    handleSelectStatus,
    handleAddHrNote,
    handleAddRaviNote,
    noticePeriodFilter,
    setNoticePeriodFilter,
    statusFilter,
    setStatusFilter,
    technologyFilter,
    setTechnologyFilter,
  };
};

export default useHiring;
